// Environment Detection Hook
// Detects if running in Electron shell or browser

import { useState, useEffect, useMemo } from 'react'
import { bridge } from '@/bridge'
import type { BridgeCapability, SystemInfoResponse } from '@abcpay/shared'

export interface Environment {
  isElectron: boolean
  platform: NodeJS.Platform | 'browser'
  capabilities: {
    printer: boolean
    counter: boolean
    fileStorage: boolean
    multiDisplay: boolean
  }
  shellVersion: string | null
  bridgeVersion: string | null
  isLoading: boolean
}

export function useEnvironment(): Environment {
  const [systemInfo, setSystemInfo] = useState<SystemInfoResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isElectron = bridge.isElectron

  useEffect(() => {
    if (!isElectron) {
      setIsLoading(false)
      return
    }

    // Fetch system info from shell
    bridge.getSystemInfo()
      .then(info => {
        setSystemInfo(info)
      })
      .catch(error => {
        console.warn('Failed to get system info:', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [isElectron])

  const capabilities = useMemo(() => {
    if (!isElectron || !systemInfo) {
      return {
        printer: false,
        counter: false,
        fileStorage: false,
        multiDisplay: false,
      }
    }

    const caps = systemInfo.capabilities || []
    return {
      printer: caps.includes('printer:serial'),
      counter: caps.includes('counter:serial'),
      fileStorage: caps.includes('storage:file'),
      multiDisplay: caps.includes('window:multiDisplay'),
    }
  }, [isElectron, systemInfo])

  return {
    isElectron,
    platform: systemInfo?.platform || (isElectron ? 'darwin' : 'browser'),
    capabilities,
    shellVersion: systemInfo?.shellVersion || null,
    bridgeVersion: systemInfo?.bridgeVersion || bridge.version,
    isLoading,
  }
}

// Simple hook to check if a feature is available
export function useFeatureAvailable(feature: BridgeCapability): boolean {
  const env = useEnvironment()

  switch (feature) {
    case 'printer:serial':
      return env.capabilities.printer
    case 'counter:serial':
      return env.capabilities.counter
    case 'storage:file':
      return env.capabilities.fileStorage
    case 'window:multiDisplay':
      return env.capabilities.multiDisplay
    default:
      return false
  }
}
